import React, { useMemo, useState } from "react";
import { DaySummary } from "../types/appTypes";
import "../styles/CalendarView.css";

type UnlockLevel = 1 | 2 | 3 | 4;

interface CalendarViewProps {
  selectedDate: string;
  onChangeDate: (date: string) => void;
  getDaySummary?: (date: string) => DaySummary | null;
  unlockLevel?: UnlockLevel; // 단계별 캘린더 해금
}

type DayInfo = { day: number; type: "prev" | "current" | "next" };

const clampMonth = (year: number, month: number) => {
  // month: 0~11
  let y = year;
  let m = month;
  if (m < 0) {
    y -= Math.ceil(Math.abs(m) / 12);
    m = (m % 12 + 12) % 12;
  } else if (m > 11) {
    y += Math.floor(m / 12);
    m = m % 12;
  }
  return { year: y, month: m };
};

const CalendarView: React.FC<CalendarViewProps> = ({
  selectedDate,
  onChangeDate,
  getDaySummary,
  unlockLevel = 4,
}) => {
  /** KST 기준 YYYY-MM-DD */
  const getKSTDateString = (date: Date) => {
    const offset = new Date(date.getTime() - date.getTimezoneOffset() * 60000);
    return offset.toISOString().split("T")[0];
  };

  const today = useMemo(() => new Date(), []);
  const todayYear = today.getFullYear();
  const todayMonth = today.getMonth();

  /** 현재 렌더링되는 연/월 */
  const [current, setCurrent] = useState(() => {
    const d = new Date(selectedDate);
    return { year: d.getFullYear(), month: d.getMonth() };
  });

  /** 단계별 월 이동 제한 */
  const isMonthAllowed = (year: number, month: number) => {
    // 비교를 위해 "월 인덱스"로 변환
    const toIndex = (y: number, m: number) => y * 12 + m;
    const curIdx = toIndex(year, month);
    const todayIdx = toIndex(todayYear, todayMonth);

    if (unlockLevel === 4) return true;

    if (unlockLevel === 1) {
      // 현재 달만
      return curIdx === todayIdx;
    }

    if (unlockLevel === 2) {
      // 이번 달 + 지난달
      return curIdx >= todayIdx - 1 && curIdx <= todayIdx;
    }

    // unlockLevel === 3
    // 최근 6개월(이번달 포함) + 다음달 1개
    return curIdx >= todayIdx - 5 && curIdx <= todayIdx + 1;
  };

  /** 월 이동 */
  const changeMonth = (diff: number) => {
    setCurrent((prev) => {
      const next = clampMonth(prev.year, prev.month + diff);
      if (!isMonthAllowed(next.year, next.month)) return prev;
      return next;
    });
  };

  /** 달력 42칸 생성 */
  const getDays = (year: number, month: number): DayInfo[] => {
    const firstDay = new Date(year, month, 1).getDay();
    const lastDate = new Date(year, month + 1, 0).getDate();
    const prevLastDate = new Date(year, month, 0).getDate();

    const days: DayInfo[] = [];

    // prev
    for (let i = firstDay - 1; i >= 0; i--) {
      days.push({ day: prevLastDate - i, type: "prev" });
    }
    // current
    for (let d = 1; d <= lastDate; d++) {
      days.push({ day: d, type: "current" });
    }
    // next
    while (days.length < 42) {
      days.push({
        day: days.length - (firstDay + lastDate) + 1,
        type: "next",
      });
    }
    return days;
  };

  const days = useMemo(() => getDays(current.year, current.month), [current]);

  const isSelected = (y: number, m: number, d: number) =>
    getKSTDateString(new Date(y, m, d)) === selectedDate;

  /** 날짜 클릭 */
  const handleClick = (info: DayInfo) => {
    let y = current.year;
    let m = current.month;

    if (info.type === "prev") {
      const prevMonth = clampMonth(y, m - 1);
      y = prevMonth.year;
      m = prevMonth.month;
    } else if (info.type === "next") {
      const nextMonth = clampMonth(y, m + 1);
      y = nextMonth.year;
      m = nextMonth.month;
    }

    // 이동한 월이 잠금 상태면 클릭 무시
    if (!isMonthAllowed(y, m)) return;

    const newDate = getKSTDateString(new Date(y, m, info.day));
    onChangeDate(newDate);
    setCurrent({ year: y, month: m });
  };

  /** 오늘로 이동 (잠금 고려) */
  const goToday = () => {
    if (!isMonthAllowed(todayYear, todayMonth)) return;
    const ds = getKSTDateString(today);
    onChangeDate(ds);
    setCurrent({ year: todayYear, month: todayMonth });
  };

  return (
    <div className="calendar-root">
      {/* 헤더 */}
      <div className="calendar-header">
        <button className="cal-nav" onClick={() => changeMonth(-1)} aria-label="prev month">
          ‹
        </button>

        <div className="calendar-title">
          <button className="cal-today" onClick={goToday}>
            오늘
          </button>
          <span className="cal-ym">
            {current.year}년 {current.month + 1}월
          </span>
        </div>

        <button className="cal-nav" onClick={() => changeMonth(1)} aria-label="next month">
          ›
        </button>
      </div>

      {/* 요일 */}
      <div className="calendar-weekdays">
        {["일", "월", "화", "수", "목", "금", "토"].map((d) => (
          <div key={d}>{d}</div>
        ))}
      </div>

      {/* 날짜 */}
      <div className="calendar-grid">
        {days.map((info, idx) => {
          let y = current.year;
          let m = current.month;

          if (info.type === "prev") {
            const pm = clampMonth(y, m - 1);
            y = pm.year;
            m = pm.month;
          } else if (info.type === "next") {
            const nm = clampMonth(y, m + 1);
            y = nm.year;
            m = nm.month;
          }

          const dateStr = getKSTDateString(new Date(y, m, info.day));
          const selected = isSelected(y, m, info.day);

          const summary =
            info.type === "current" && getDaySummary ? getDaySummary(dateStr) : null;

          const doneDots = summary ? Math.min(summary.donelength, 4) : 0;
          const todoDots = summary
            ? Math.min(Math.max(summary.totaldone - summary.donelength, 0), 4 - doneDots)
            : 0;
          const extra = summary ? Math.max(summary.totaldone - 4, 0) : 0;

          // 잠금 월이면 셀도 잠금 처리
          const lockedMonth = !isMonthAllowed(y, m);

          return (
            <div
              key={idx}
              className={[
                "calendar-cell",
                info.type,
                selected ? "selected" : "",
                lockedMonth ? "locked" : "",
              ].join(" ")}
              onClick={() => handleClick(info)}
            >
              <div className="calendar-day">{info.day}</div>

              {summary && (
                <div className="calendar-dots">
                  {Array.from({ length: doneDots }).map((_, i) => (
                    <span key={`done-${dateStr}-${i}`} className="dot done" />
                  ))}

                  {Array.from({ length: todoDots }).map((_, i) => (
                    <span key={`todo-${dateStr}-${i}`} className="dot todo" />
                  ))}

                  {extra > 0 && <span className="dot-more">+{extra}</span>}

                  {summary.hasDiary && (
                    <span className="diary-badge" title="일기 있음">
                      🏷️
                    </span>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default CalendarView;
